import React, { useEffect, useState } from 'react';
import Modal from '../../common/Modal';
import SaveCancelButtons from '../../common/buttons/SaveCancelButtons';
import { formatNamespaceForProject } from '../../../utils/project-utils';
import Label from '../../common/Label';
import { styled } from 'styled-components';
import { fetchNextProjectNumber } from '../../../services/project-service';
import BusySpinner from '../../common/BusySpinner';
import { ProjectTypes } from '../../../data/models/project';
import CircularProgress from '@mui/material/CircularProgress';

interface ProjectCreationConfirmationModalProps {
	open: boolean;
	handleSave: () => void;
	handleClose: () => void;
	previewData: any;
}
export default function ProjectCreationConfirmationModal(
	props: ProjectCreationConfirmationModalProps
) {
	const { open, handleSave, handleClose, previewData } = props;

	const [busy, setBusy] = useState(false);
	const [projectNumber, setProjectNumber] = useState<number | null>(null);

	useEffect(() => {
		let isMounted = true;

		const fetchProjectNumber = async () => {
			setBusy(true);
			try {
				const number = await fetchNextProjectNumber();
				if (isMounted) {
					setProjectNumber(number || null);
				}
			} catch (e) {
				console.error(e);
			} finally {
				// Ensure spinner shows for at least 500ms for better UX
				setTimeout(() => {
					if (isMounted) {
						setBusy(false);
					}
				}, 500);
			}
		};

		if (open) {
			fetchProjectNumber();
		}

		return () => {
			isMounted = false;
		};
	}, [open]);

	return (
		<Modal open={open} handleClose={handleClose}>
			{open ? (
				<>
					{busy ? (
						<BusySpinnerContainer>
							<CircularProgress />
						</BusySpinnerContainer>
					) : (
						<ModalContent>
							<Label>Project Namespace</Label>
							<Namespace>
								{formatNamespaceForProject(
									projectNumber,
									previewData.client,
									previewData.name
								)}
							</Namespace>
							<Label>Project Type</Label>
							<ProjectType>
								{ProjectTypes[previewData.type]?.display}
							</ProjectType>
							{previewData.subTypeSelections &&
								Object.keys(previewData.subTypeSelections)
									.length > 0 && (
									<>
										{Object.entries(
											previewData.subTypeSelections
										).map(
											([subTypeName, selectedValue]) => (
												<div key={subTypeName}>
													<Label>{subTypeName}</Label>
													<SubTypeValue>
														{
															selectedValue as string
														}
													</SubTypeValue>
												</div>
											)
										)}
									</>
								)}
							<Label>Workspace Members</Label>
							<WorkspaceMembers>
								{previewData.workspaceMembers
									.map((member) => member.display)
									.join(', ')}
							</WorkspaceMembers>
							<SaveCancelButtons
								handleSave={handleSave}
								handleCancel={handleClose}
								saveText={'Create'}
							/>
						</ModalContent>
					)}
				</>
			) : (
				<></>
			)}
		</Modal>
	);
}

const Namespace = styled.div`
	font-weight: bold;
	font-size: 18px;
	margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const BusySpinnerContainer = styled.div`
	padding: 50px 0;
	width: 500px;
	text-align: center;
	min-height: 150px;
`;

const ProjectType = styled.div`
	margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const SubTypeValue = styled.div`
	margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const WorkspaceMembers = styled.div`
	margin-bottom: ${(props) => props.theme.spacing(4)};
`;

const ModalContent = styled.div`
	min-width: 450px;
`;
